import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TAG_TOOLTIP_DATA } from '@simlab/simlab-facility-management/common';
import { ACCEPTED_IMAGE_EXTENSIONS } from '@simOn/common/attachment-preview';
import { AttachmentThumbnailService } from '@simOn/common/media';
import { Attachment } from '@simOn/common/models';
import { IScanControllerToken, MarkerOpenIntentService, MarkerOpenSection } from '@simOn/common/scan';
import { SpaceFacade } from '@simOn/space';
import { TicketFacade, TicketSelectors } from '@simOn/ticket';
import { TasksListApiService } from '@simOn/ticket/element/data-access';
import {
  TaskTicketDetail,
  Ticket,
  TICKET_STATUS_BASE,
  TICKET_STATUS_SELECT,
  TTicketStatus
} from '@simOn/ticket/element/models';
import { SimAttachmentTileComponent } from '@simOn/ui/sim-attachment-tile';
import { SimColorBadgeComponent } from '@simOn/ui/sim-color-badge';
import { SimIconComponent } from '@simOn/ui/sim-icon';
import { SimSpinnerComponent } from '@simOn/ui/sim-spinner';
import { catchError, filter, forkJoin, map, of, switchMap, take } from 'rxjs';
import { DueDateComponent } from '../due-date/due-date.component';

/** Figma "Ticket status" strip colors per status (background accent + left border / icon color). */
const STATUS_STRIP: Record<TTicketStatus, { background: string; color: string }> = {
  Unknown: { background: 'var(--bg-gray, #f6f7f9)', color: 'var(--text-icon-lighter, #566276)' },
  Open: { background: 'var(--status-info-background, #e0e9f9)', color: 'var(--status-info-default, #4670db)' },
  InProgress: { background: 'var(--status-warn-background, #fff6e0)', color: 'var(--status-warn-default, #906500)' },
  Closed: { background: 'var(--status-success-background, #eefff6)', color: 'var(--status-success-default, #079e58)' }
};

/** Tiles shown before the "+N" overflow tile. */
const MAX_IMAGE_TILES = 2;

type DetailState = { loaded: boolean; detail: TaskTicketDetail | null };
type ImageUrlsState = { key: string; urls: string[] };

/**
 * Hover card for a ticket / task marker on the 3D scan. Rendered by `simlab-tag-tooltip-host`
 * from `ComponentConfiguration.tooltip = { component: TicketTooltipComponent }`; the ticket id
 * comes from the marker `userData` (`noteId`) via `TAG_TOOLTIP_DATA`.
 * Base data comes from the tickets store; checklist and asset/contractor links from the task detail endpoint.
 */
@Component({
  selector: 'ticket-tooltip',
  imports: [
    SimAttachmentTileComponent,
    SimColorBadgeComponent,
    SimIconComponent,
    DueDateComponent,
    SimSpinnerComponent
  ],
  templateUrl: './ticket-tooltip.component.html',
  styleUrl: './ticket-tooltip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TicketTooltipComponent {
  private readonly _data = inject(TAG_TOOLTIP_DATA);
  private readonly _store = inject(Store);
  private readonly _ticketFacade = inject(TicketFacade);
  private readonly _tasksApi = inject(TasksListApiService);
  private readonly _spaceFacade = inject(SpaceFacade);
  private readonly _thumbnails = inject(AttachmentThumbnailService);
  private readonly _scanController = inject(IScanControllerToken, { optional: true });
  private readonly _openIntent = inject(MarkerOpenIntentService);
  private readonly _destroyRef = inject(DestroyRef);

  protected readonly ticketId =
    (this._data.userData?.['noteId'] as string | undefined) ??
    (this._data.userData?.['taskId'] as string | undefined) ??
    this._data.id;
  protected readonly canShowMore = !!this._scanController;

  protected readonly ticket: Signal<Ticket | undefined> = toSignal(
    this._store.select(TicketSelectors.GetSelectedTicketById(this.ticketId)),
    { initialValue: undefined }
  );

  /** Checklist and asset / contractor links live only in the task detail endpoint. */
  private readonly _detailState: Signal<DetailState> = toSignal(
    this._spaceFacade.selectedApartmentId$.pipe(
      filter((apartmentId): apartmentId is string => !!apartmentId),
      take(1),
      switchMap((apartmentId) => this._tasksApi.getTicket(this.ticketId, apartmentId)),
      map((detail): DetailState => ({ loaded: true, detail })),
      catchError(() => of<DetailState>({ loaded: true, detail: null }))
    ),
    { initialValue: { loaded: false, detail: null } }
  );
  protected readonly detail = computed(() => this._detailState().detail);

  protected readonly status = computed<TTicketStatus>(() => this.ticket()?.status ?? 'Unknown');
  protected readonly statusLabel = computed(() => TICKET_STATUS_BASE[this.status()].label);
  protected readonly statusIcon = computed(() => TICKET_STATUS_BASE[this.status()].icon);
  protected readonly statusStrip = computed(() => STATUS_STRIP[this.status()]);
  protected readonly dueDateColor = computed(() => TICKET_STATUS_SELECT[this.status()].dueDateColor);
  protected readonly dueDate = computed(() => this.ticket()?.dueDate ?? null);
  protected readonly isFinished = computed(() => this.status() === 'Closed');

  protected readonly description = computed(() => htmlToText(this.ticket()?.htmlText ?? ''));

  protected readonly images = computed<Attachment[]>(() =>
    (this.ticket()?.attachments ?? []).filter((attachment) => isImage(attachment.name))
  );
  protected readonly visibleImages = computed(() => {
    const images = this.images();
    return images.length > MAX_IMAGE_TILES + 1 ? images.slice(0, MAX_IMAGE_TILES) : images;
  });
  protected readonly moreImages = computed(() => Math.max(0, this.images().length - this.visibleImages().length));

  private readonly _imagesKey = computed(() =>
    this.visibleImages()
      .map((image) => image.id)
      .join()
  );
  /** Object urls for the visible image tiles, in the same order, keyed by the images they belong to. */
  private readonly _imageUrlsState: Signal<ImageUrlsState> = toSignal(
    toObservable(this._imagesKey).pipe(
      switchMap((key) => {
        const images = this.visibleImages();
        if (images.length === 0) return of<ImageUrlsState>({ key, urls: [] });
        return forkJoin(images.map((image) => this._thumbnails.thumbnailUrl$(image))).pipe(
          map((urls): ImageUrlsState => ({ key, urls }))
        );
      })
    ),
    { initialValue: { key: '\u0000', urls: [] } }
  );
  protected readonly imageUrls = computed(() => this._imageUrlsState().urls);

  /** True when the store ticket is complete, the detail request settled and thumbnails are resolved. */
  protected readonly ready = computed(() => {
    const ticket = this.ticket();
    const ticketComplete = !!ticket && ticket.attachments !== undefined && ticket.htmlText !== undefined;
    const imagesReady = this._imageUrlsState().key === this._imagesKey();
    return ticketComplete && this._detailState().loaded && imagesReady;
  });

  protected readonly checklist = computed(() => {
    const items = this.detail()?.checklist?.items ?? [];
    if (items.length === 0) return null;
    const completed = items.filter((item) => item.isChecked).length;
    return { completed, total: items.length, percent: Math.round((completed / items.length) * 100) };
  });
  protected readonly hasAsset = computed(() => !!this.detail()?.assetId);
  protected readonly hasContractor = computed(() => !!this.detail()?.contractorId);

  constructor() {
    this._destroyRef.onDestroy(() => this.imageUrls().forEach((url) => this._thumbnails.revoke(url)));
    this._ensureTicketLoaded();
  }

  protected checklistLabel(completed: number, total: number): string {
    return $localize`:@@TASKS_PREVIEW_ITEMS_COMPLETED:${completed}:PH_DONE: / ${total}:PH_TOTAL: Items Completed`;
  }

  /** Opens the ticket side sheet; with `section` it scrolls to the photos section. */
  showMore(section: MarkerOpenSection | null = null): void {
    if (!this._scanController) return;
    this._openIntent.set(section);
    this._scanController.openMarker({
      id: this._data.id,
      userData: this._data.userData ?? { type: 'tasks', noteId: this.ticketId, taskId: this.ticketId }
    });
  }

  /** The tickets list may hold a slim entry; the detail action upserts the full ticket. */
  private _ensureTicketLoaded(): void {
    const ticket = this.ticket();
    if (!this.ticketId) return;
    if (!ticket || ticket.attachments === undefined || ticket.htmlText === undefined) {
      this._ticketFacade.getTicket(this.ticketId);
    }
  }
}

function isImage(fileName: string | undefined): boolean {
  const extension = fileName?.split('.').pop()?.toLowerCase() ?? '';
  return (ACCEPTED_IMAGE_EXTENSIONS as readonly string[]).includes(extension);
}

function htmlToText(html: string): string {
  if (!html) return '';
  const text = new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';
  return text.replace(/\s+/g, ' ').trim();
}
